import { GoogleTask, GoogleTaskList, TaskListWithTasks } from '../domain';

/**
 * Read-only access to the user's Google Tasks, for the import experiment.
 *
 * Auth deliberately does *not* go through Firebase Auth. Firebase's GoogleAuthProvider
 * hands back an access token only at the moment of sign-in, and a returning session has
 * none — so reusing it would mean forcing a re-authentication just to run an import.
 * Instead this uses a Google Identity Services token client, which asks for the Tasks
 * scope on demand, at the moment the user clicks Scan. The token lives in memory only.
 *
 * A one-shot import needs no refresh token and no backend: everything here runs in the
 * browser against tasks.googleapis.com. Continuous two-way sync would need both.
 */

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const TASKS_API_BASE = 'https://tasks.googleapis.com/tasks/v1';
const TASKS_SCOPE = 'https://www.googleapis.com/auth/tasks.readonly';

/** Google caps maxResults at 100; the guard stops a runaway loop on an unexpected response. */
const PAGE_SIZE = 100;
const MAX_PAGES = 50;
/** How far back completion history is read. Older occurrences don't improve the estimate. */
const HISTORY_MONTHS = 24;

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}

interface GoogleAccountsOAuth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }) => TokenClient;
}

declare global {
  interface Window {
    google?: { accounts?: { oauth2?: GoogleAccountsOAuth2 } };
  }
}

interface TaskListsResponse {
  items?: GoogleTaskList[];
  nextPageToken?: string;
}

interface TasksResponse {
  items?: GoogleTask[];
  nextPageToken?: string;
}

export class GoogleTasksService {
  private scriptPromise: Promise<void> | null = null;
  private token: { value: string; expiresAt: number } | null = null;

  /** True once a token has been granted this session — drives the UI's connected state. */
  isConnected(): boolean {
    return this.token !== null && this.token.expiresAt > Date.now();
  }

  /** Drops the in-memory token. Google-side consent is unaffected. */
  disconnect(): void {
    this.token = null;
  }

  /**
   * Fetches every task list with all of its tasks, including completed and hidden ones.
   *
   * Completed instances are the whole point: they are the only record of how often a
   * recurring task actually came round, since the API exposes no recurrence rule.
   */
  async fetchAllTaskLists(): Promise<TaskListWithTasks[]> {
    const token = await this.getAccessToken();
    const lists = await this.listTaskLists(token);

    const result: TaskListWithTasks[] = [];
    // Sequential rather than parallel — gentler on quota, and list counts are small.
    for (const list of lists) {
      result.push({ list, ...(await this.listTasks(token, list.id)) });
    }
    return result;
  }

  private async listTaskLists(token: string): Promise<GoogleTaskList[]> {
    const lists: GoogleTaskList[] = [];
    let pageToken: string | undefined;
    let page = 0;

    do {
      const response: TaskListsResponse = await this.apiGet('/users/@me/lists', token, {
        maxResults: String(PAGE_SIZE),
        ...(pageToken ? { pageToken } : {}),
      });
      lists.push(...(response.items ?? []));
      pageToken = response.nextPageToken;
    } while (pageToken && ++page < MAX_PAGES);

    return lists;
  }

  /**
   * Reads a list's open tasks plus its recent completion history.
   *
   * Two passes rather than one because completedMin filters on completion date, which
   * would drop every still-open task. Bounding the history matters on long-lived
   * accounts, where years of completed one-off todos otherwise dwarf the recurring
   * tasks that are actually worth importing.
   */
  private async listTasks(
    token: string,
    listId: string,
  ): Promise<{ tasks: GoogleTask[]; truncated: boolean }> {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - HISTORY_MONTHS);

    const open = await this.pageThroughTasks(token, listId, { showCompleted: 'false' });
    const completed = await this.pageThroughTasks(token, listId, {
      showCompleted: 'true',
      // Completed tasks are hidden by default; without this the history is invisible.
      showHidden: 'true',
      completedMin: cutoff.toISOString(),
    });

    // A task is either open or completed, so the passes should not overlap — dedupe by
    // id rather than depend on that.
    const byId = new Map<string, GoogleTask>();
    for (const task of [...open.tasks, ...completed.tasks]) {
      byId.set(task.id, task);
    }

    return {
      tasks: [...byId.values()],
      truncated: open.truncated || completed.truncated,
    };
  }

  private async pageThroughTasks(
    token: string,
    listId: string,
    params: Record<string, string>,
  ): Promise<{ tasks: GoogleTask[]; truncated: boolean }> {
    const tasks: GoogleTask[] = [];
    let pageToken: string | undefined;
    let page = 0;

    do {
      const response: TasksResponse = await this.apiGet(
        `/lists/${encodeURIComponent(listId)}/tasks`,
        token,
        {
          maxResults: String(PAGE_SIZE),
          showDeleted: 'false',
          ...params,
          ...(pageToken ? { pageToken } : {}),
        },
      );
      tasks.push(...(response.items ?? []));
      pageToken = response.nextPageToken;
    } while (pageToken && ++page < MAX_PAGES);

    // Stopping with a page token still in hand means the cap cut the read short, and
    // the caller is looking at partial history without knowing it.
    return { tasks, truncated: Boolean(pageToken) };
  }

  private async apiGet<T>(path: string, token: string, params: Record<string, string>): Promise<T> {
    const url = new URL(`${TASKS_API_BASE}${path}`);
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }

    const response = await fetch(url.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      // A rejected token is worth discarding so the next attempt re-prompts.
      if (response.status === 401 || response.status === 403) {
        this.token = null;
      }
      const body = await response.text();
      throw new Error(`Google Tasks API returned ${response.status}: ${body.slice(0, 300)}`);
    }

    return response.json() as Promise<T>;
  }

  private async getAccessToken(): Promise<string> {
    // Refresh a minute early so a scan can't start with a token that expires mid-flight.
    if (this.token && this.token.expiresAt > Date.now() + 60_000) {
      return this.token.value;
    }

    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      throw new Error(
        'VITE_GOOGLE_OAUTH_CLIENT_ID is not set. Create an OAuth 2.0 Web client in the ' +
        'Google Cloud console, enable the Tasks API, add this origin to its authorised ' +
        'JavaScript origins, and put the client ID in .env.local.',
      );
    }

    await this.loadGisScript();
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) {
      throw new Error('Google Identity Services loaded but did not initialise.');
    }

    return new Promise<string>((resolve, reject) => {
      const client = oauth2.initTokenClient({
        client_id: clientId,
        scope: TASKS_SCOPE,
        callback: (response) => {
          if (response.error || !response.access_token) {
            reject(new Error(
              response.error_description || response.error || 'Google refused the token request.',
            ));
            return;
          }
          this.token = {
            value: response.access_token,
            expiresAt: Date.now() + (response.expires_in ?? 3600) * 1000,
          };
          resolve(response.access_token);
        },
        error_callback: (error) => {
          reject(new Error(error.message || 'Google authorisation was dismissed.'));
        },
      });

      client.requestAccessToken();
    });
  }

  /** Loads the GIS script on first use, so the app pays nothing for it elsewhere. */
  private loadGisScript(): Promise<void> {
    if (window.google?.accounts?.oauth2) return Promise.resolve();
    if (this.scriptPromise) return this.scriptPromise;

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // Allow a retry after a transient network failure.
        this.scriptPromise = null;
        reject(new Error('Could not load Google Identity Services.'));
      };
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }
}

export const googleTasksService = new GoogleTasksService();
export default googleTasksService;
